import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';
import type { AssistantToolCallResult, WorkflowStepEvent, NLSearchResponse, EntityExtractionResult, ContextPin } from '../api/client';
import type { CampaignData } from '../../shared/types';
import type { ChatMessage, SuggestedItem, AssistantOperation } from '../types/ui';

const CHAT_STORAGE_KEY = 'campaign-assistant-chat';
const OFFLINE_QUEUE_STORAGE_KEY = 'campaign-assistant-offline-queue';
const OFFLINE_QUEUE_LAST_SYNC_KEY = 'campaign-assistant-offline-last-sync';
const BRANCH_STORAGE_KEY = 'campaign-assistant-branches';
const THREAD_ID_STORAGE_KEY = 'campaign-assistant-thread-id';
const MAX_STORED_MESSAGES = 50;
const MAX_QUEUED_PROMPTS = 40;

export interface FollowUpSuggestion {
  text: string;
  prompt: string;
}

export interface QuickAction {
  id: string;
  label: string;
  icon: string;
  prompt: string;
}

export interface OfflineAssistantQueueItem {
  id: string;
  prompt: string;
  queuedAt: string;
  source: 'chat' | 'quick_action' | 'follow_up' | 'retry';
  attempts: number;
  messageAlreadyInChat: boolean;
}

type SendMessageStatus = 'sent' | 'queued' | 'failed';
type SendMessageOptions = {
  source?: OfflineAssistantQueueItem['source'];
  skipUserMessage?: boolean;
  requeueOnConnectivityLoss?: boolean;
};

function parseStoredOfflineQueue(value: string | null): OfflineAssistantQueueItem[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        if (
          !entry
          || typeof entry !== 'object'
          || typeof entry.id !== 'string'
          || typeof entry.prompt !== 'string'
          || typeof entry.queuedAt !== 'string'
        ) {
          return null;
        }

        return {
          id: entry.id,
          prompt: entry.prompt,
          queuedAt: entry.queuedAt,
          source:
            entry.source === 'quick_action'
            || entry.source === 'follow_up'
            || entry.source === 'retry'
              ? entry.source
              : 'chat',
          attempts: typeof entry.attempts === 'number' && Number.isFinite(entry.attempts) ? entry.attempts : 0,
          messageAlreadyInChat: Boolean(entry.messageAlreadyInChat),
        } satisfies OfflineAssistantQueueItem;
      })
      .filter((entry): entry is OfflineAssistantQueueItem => entry != null);
  } catch {
    return [];
  }
}

function truncatePrompt(prompt: string): string {
  const normalized = prompt.trim().replace(/\s+/g, ' ');
  if (normalized.length <= 110) return normalized;
  return `${normalized.slice(0, 107)}...`;
}

function isConnectivityError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes('cannot connect to server')
    || message.includes('failed to fetch')
    || message.includes('networkerror')
    || message.includes('network request failed')
    || message.includes('fetch failed')
    || message.includes('offline')
  );
}

export const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'session-planning',
    label: 'Plan Next Session',
    icon: '📋',
    prompt: 'Help me plan my next session. Based on my active plot threads and where things left off, what are some good directions I could take? What loose ends should I consider addressing?',
  },
  {
    id: 'campaign-review',
    label: 'Campaign Review',
    icon: '🔍',
    prompt: 'Review my campaign so far. Are there any plot threads I might be neglecting? NPCs that could be used more effectively? Opportunities I might be missing?',
  },
  {
    id: 'player-engagement',
    label: 'Player Engagement',
    icon: '🎭',
    prompt: 'I want to better engage my players. Based on my party composition and current storylines, what are some ways I could give each character more spotlight moments or personal stakes?',
  },
  {
    id: 'npc-development',
    label: 'Develop an NPC',
    icon: '👤',
    prompt: 'Help me develop an NPC for my campaign. Before we create them, I\'d like to discuss what role they should serve and how they might connect to existing elements.',
  },
  {
    id: 'encounter-design',
    label: 'Design Encounter',
    icon: '⚔️',
    prompt: 'Help me design a memorable encounter. What kind of challenge am I looking for? Combat, social, puzzle, or a mix? What should it accomplish narratively?',
  },
  {
    id: 'plot-advice',
    label: 'Plot Advice',
    icon: '📜',
    prompt: 'I need advice on my plot threads. Which ones seem strongest? Are there natural ways to connect them? What twists or developments might make them more compelling?',
  },
];

// ── Tool call → SuggestedItem conversion ────────────────────────────

function toolCallsToSuggestions(toolCalls: AssistantToolCallResult[]): SuggestedItem[] {
  const items: SuggestedItem[] = [];
  for (const tc of toolCalls) {
    const args = tc.arguments;
    switch (tc.name) {
      case 'navigate_to_tab':
        items.push({
          type: 'action_navigate',
          target: String(args.target || ''),
          ...(args.entity ? { entity: String(args.entity) } : {}),
        });
        break;
      case 'quick_add':
        items.push({
          type: 'action_quick_add',
          category: String(args.category || ''),
        });
        break;
      case 'update_party_heat':
        items.push({
          type: 'action_update_heat',
          value: Number(args.value) || 0,
          operation: String(args.operation || 'set'),
        });
        break;
      case 'search_campaign':
        items.push({
          type: 'action_navigate',
          target: 'overview',
          entity: String(args.query || ''),
        });
        break;
      // generate_follow_ups handled separately
    }
  }
  return items;
}

function toolCallsToFollowUps(toolCalls: AssistantToolCallResult[]): FollowUpSuggestion[] {
  for (const tc of toolCalls) {
    if (tc.name === 'generate_follow_ups') {
      const suggestions = tc.arguments.suggestions;
      if (Array.isArray(suggestions)) {
        return suggestions
          .slice(0, 3)
          .map((s) => ({
            text: String((s as Record<string, unknown>).label || ''),
            prompt: String((s as Record<string, unknown>).prompt || ''),
          }))
          .filter((s) => s.text && s.prompt);
      }
    }
  }
  return [];
}

// ── CRUD tool call → AssistantOperation conversion ──────────────────

function generateOperationId(): string {
  return `op-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

const CRUD_TOOL_NAMES = new Set([
  'entity_create', 'entity_update', 'entity_delete', 'entity_read', 'entity_bulk',
]);

// Keys that are tool-call metadata, not entity field data
const TOOL_CALL_META_KEYS = new Set([
  'entityType', 'entityId', 'parentId', 'autoExecute', 'reason', 'previousSnapshot',
  'action', 'data', 'operations',
]);

/** Extract entity data from args — handles both `args.data` and flattened fields at root */
function extractData(args: Record<string, unknown>): Record<string, unknown> | undefined {
  if (args.data && typeof args.data === 'object') {
    return args.data as Record<string, unknown>;
  }
  // Fallback: collect non-metadata fields as the data object
  const fallback: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(args)) {
    if (!TOOL_CALL_META_KEYS.has(key) && value !== undefined) {
      fallback[key] = value;
    }
  }
  return Object.keys(fallback).length > 0 ? fallback : undefined;
}

function toolCallsToCrudOperations(toolCalls: AssistantToolCallResult[]): AssistantOperation[] {
  const ops: AssistantOperation[] = [];
  for (const tc of toolCalls) {
    if (!CRUD_TOOL_NAMES.has(tc.name)) continue;
    const args = tc.arguments;

    if (tc.name === 'entity_bulk' && Array.isArray(args.operations)) {
      for (const bulkOp of args.operations as Array<Record<string, unknown>>) {
        const action = bulkOp.action as 'create' | 'update' | 'delete';
        if (!action || action === 'read' as string) continue;
        ops.push({
          id: generateOperationId(),
          action,
          entityType: String(bulkOp.entityType || ''),
          entityId: typeof bulkOp.entityId === 'number' ? bulkOp.entityId : undefined,
          data: extractData(bulkOp),
          status: 'pending',
          autoExecute: Boolean(args.autoExecute) && action !== 'delete',
          reason: typeof bulkOp.reason === 'string' ? bulkOp.reason : undefined,
          previousSnapshot: (bulkOp.previousSnapshot as Record<string, unknown>) ?? undefined,
        });
      }
      continue;
    }

    if (tc.name === 'entity_read') continue; // read ops don't create mutations

    const action = tc.name === 'entity_create' ? 'create'
      : tc.name === 'entity_update' ? 'update'
      : 'delete';

    ops.push({
      id: generateOperationId(),
      action,
      entityType: String(args.entityType || ''),
      entityId: typeof args.entityId === 'number' ? args.entityId : undefined,
      data: extractData(args),
      parentId: typeof args.parentId === 'number' ? args.parentId : undefined,
      status: 'pending',
      autoExecute: Boolean(args.autoExecute) && action !== 'delete',
      reason: typeof args.reason === 'string' ? args.reason : undefined,
      previousSnapshot: (args.previousSnapshot as Record<string, unknown>) ?? undefined,
    });
  }
  return ops;
}

// ── Tag-based parsing (kept for backward compatibility + content creation) ──

function extractField(text: string, fieldName: string) {
  const regex = new RegExp(`${fieldName}:\\s*(.+?)(?=\\n[A-Z][a-z]+:|$)`, 's');
  const match = text.match(regex);
  return match ? match[1].trim() : '';
}

function parseTagSuggestions(text: string, sessionNumber: number): SuggestedItem[] {
  const suggestions: SuggestedItem[] = [];
  let match: RegExpExecArray | null;

  // Parse Player Characters
  const pcRegex = /\[PC\]([\s\S]*?)\[\/PC\]/g;
  while ((match = pcRegex.exec(text)) !== null) {
    const pcText = match[1];
    const pc: SuggestedItem = {
      type: 'pc',
      characterName: extractField(pcText, 'CharacterName'),
      playerName: extractField(pcText, 'PlayerName'),
      class: extractField(pcText, 'Class'),
      level: parseInt(extractField(pcText, 'Level')) || 1,
      faction: extractField(pcText, 'Faction'),
      personalHeat: 0,
      heatNotes: '',
      notes: extractField(pcText, 'Notes')
    };
    if (pc.characterName) suggestions.push(pc);
  }

  // Parse NPCs
  const npcRegex = /\[NPC\]([\s\S]*?)\[\/NPC\]/g;
  while ((match = npcRegex.exec(text)) !== null) {
    const npcText = match[1];
    const npc: SuggestedItem = {
      type: 'npc',
      name: extractField(npcText, 'Name'),
      description: extractField(npcText, 'Description'),
      location: extractField(npcText, 'Location'),
      notes: extractField(npcText, 'Notes')
    };
    if (npc.name) suggestions.push(npc);
  }

  // Parse Locations
  const locationRegex = /\[LOCATION\]([\s\S]*?)\[\/LOCATION\]/g;
  while ((match = locationRegex.exec(text)) !== null) {
    const locText = match[1];
    const location: SuggestedItem = {
      type: 'location',
      name: extractField(locText, 'Name'),
      description: extractField(locText, 'Description'),
      connections: extractField(locText, 'Connections'),
      notes: extractField(locText, 'Notes')
    };
    if (location.name) suggestions.push(location);
  }

  // Parse Organizations
  const orgRegex = /\[ORGANIZATION\]([\s\S]*?)\[\/ORGANIZATION\]/g;
  while ((match = orgRegex.exec(text)) !== null) {
    const orgText = match[1];
    const organization: SuggestedItem = {
      type: 'organization',
      name: extractField(orgText, 'Name'),
      type_value: extractField(orgText, 'Type'),
      goals: extractField(orgText, 'Goals'),
      members: extractField(orgText, 'Members'),
      notes: extractField(orgText, 'Notes')
    };
    if (organization.name) suggestions.push(organization);
  }

  // Parse Plot Threads
  const plotRegex = /\[PLOT\]([\s\S]*?)\[\/PLOT\]/g;
  while ((match = plotRegex.exec(text)) !== null) {
    const plotText = match[1];
    const plot: SuggestedItem = {
      type: 'plot',
      title: extractField(plotText, 'Title'),
      description: extractField(plotText, 'Description'),
      status: extractField(plotText, 'Status') || 'Active',
      priority: extractField(plotText, 'Priority') || 'Medium'
    };
    if (plot.title) suggestions.push(plot);
  }

  // Parse Future Content
  const futureRegex = /\[FUTURE\]([\s\S]*?)\[\/FUTURE\]/g;
  while ((match = futureRegex.exec(text)) !== null) {
    const futureText = match[1];
    const future: SuggestedItem = {
      type: 'future',
      title: extractField(futureText, 'Title'),
      type_value: extractField(futureText, 'Type') || 'Other',
      description: extractField(futureText, 'Description'),
      prepNotes: extractField(futureText, 'PrepNotes')
    };
    if (future.title) suggestions.push(future);
  }

  // Parse Wiki Articles
  const wikiRegex = /\[WIKI\]([\s\S]*?)\[\/WIKI\]/g;
  while ((match = wikiRegex.exec(text)) !== null) {
    const wikiText = match[1];
    const wikiSuggestion: SuggestedItem = {
      type: 'wiki',
      title: extractField(wikiText, 'Title'),
      articleType: extractField(wikiText, 'Type'),
      summary: extractField(wikiText, 'Summary'),
      content: extractField(wikiText, 'Content'),
      tags: extractField(wikiText, 'Tags'),
      visibility: extractField(wikiText, 'Visibility') || 'public',
    };
    if (wikiSuggestion.title || wikiSuggestion.summary || wikiSuggestion.content) {
      suggestions.push(wikiSuggestion);
    }
  }

  // Parse Session Notes
  const sessionRegex = /\[SESSION\]([\s\S]*?)\[\/SESSION\]/g;
  while ((match = sessionRegex.exec(text)) !== null) {
    const sessionText = match[1];
    const session: SuggestedItem = {
      type: 'session',
      sessionNum: parseInt(extractField(sessionText, 'SessionNum')) || sessionNumber || 1,
      date: extractField(sessionText, 'Date'),
      summary: extractField(sessionText, 'Summary'),
      highlights: extractField(sessionText, 'Highlights'),
    };
    if (session.summary) suggestions.push(session);
  }

  // Parse Session Links
  const sessionLinkRegex = /\[SESSION_LINK\]([\s\S]*?)\[\/SESSION_LINK\]/g;
  while ((match = sessionLinkRegex.exec(text)) !== null) {
    const linkText = match[1];
    const category = extractField(linkText, 'Category') as 'npcs' | 'locations' | 'organizations' | 'plotThreads';
    const itemName = extractField(linkText, 'ItemName');
    const sessionNum = parseInt(extractField(linkText, 'SessionNum')) || 0;
    if (category && itemName && sessionNum) {
      suggestions.push({
        type: 'session_link',
        sessionNum,
        category,
        itemName,
      });
    }
  }

  // Parse Actions (legacy tag-based, kept for backward compatibility)
  const actionRegex = /\[ACTION\]([\s\S]*?)\[\/ACTION\]/g;
  while ((match = actionRegex.exec(text)) !== null) {
    const actionText = match[1];
    const type = extractField(actionText, 'Type').toLowerCase();

    if (type === 'navigate') {
      const target = extractField(actionText, 'Target').toLowerCase();
      const entity = extractField(actionText, 'Entity');
      suggestions.push({
        type: 'action_navigate',
        target,
        ...(entity ? { entity } : {}),
      });
    } else if (type === 'quick_add') {
      suggestions.push({
        type: 'action_quick_add',
        category: extractField(actionText, 'Category'),
      });
    } else if (type === 'update_heat') {
      suggestions.push({
        type: 'action_update_heat',
        value: parseInt(extractField(actionText, 'Value')) || 0,
        operation: extractField(actionText, 'Operation'),
      });
    }
  }

  return suggestions;
}

// ── Rule-based follow-up generation (fallback when AI doesn't provide them) ──

function generateRuleBasedFollowUps(responseText: string): FollowUpSuggestion[] {
  const followUps: FollowUpSuggestion[] = [];
  const lowerResponse = responseText.toLowerCase();

  if (responseText.includes('?')) {
    followUps.push({
      text: 'Yes, let\'s do that',
      prompt: 'Yes, I like that direction. Let\'s go with it and flesh it out more.',
    });
  }

  if (responseText.includes('[NPC]') || lowerResponse.includes('npc')) {
    followUps.push({
      text: 'How would they react to the party?',
      prompt: 'How might this NPC react to meeting my party? What would their first impression be of each character?',
    });
  }

  if (responseText.includes('[LOCATION]') || lowerResponse.includes('location')) {
    followUps.push({
      text: 'What secrets does this place hold?',
      prompt: 'What secrets or hidden elements could this location have that the party might discover?',
    });
  }

  if (responseText.includes('[PLOT]') || lowerResponse.includes('plot')) {
    followUps.push({
      text: 'What could go wrong?',
      prompt: 'What complications or unexpected turns could arise from this plot thread? How might things go wrong in interesting ways?',
    });
  }

  if (lowerResponse.includes('encounter') || lowerResponse.includes('combat') || lowerResponse.includes('fight')) {
    followUps.push({
      text: 'How can I make it memorable?',
      prompt: 'What can I do to make this encounter more memorable beyond just the mechanics? Environmental elements, dramatic moments, or consequences?',
    });
  }

  if (lowerResponse.includes('session') && (lowerResponse.includes('plan') || lowerResponse.includes('prep') || lowerResponse.includes('next'))) {
    followUps.push({
      text: 'Link relevant items to this session',
      prompt: 'Based on what we discussed for session planning, suggest which existing NPCs, locations, organizations, and plot threads should be linked to this session for quick reference. Use the [SESSION_LINK] tags.',
    });
  }

  if (followUps.length < 3) {
    followUps.push({
      text: 'What should I watch out for?',
      prompt: 'What potential pitfalls or challenges should I be aware of with this approach? Any common mistakes to avoid?',
    });
  }

  if (followUps.length < 3) {
    followUps.push({
      text: 'Give me alternatives',
      prompt: 'What are some alternative approaches I could consider? What would be different about each option?',
    });
  }

  if (followUps.length < 3) {
    followUps.push({
      text: 'How do I run this?',
      prompt: 'What practical advice do you have for running this at the table? Any tips for pacing, presentation, or handling player choices?',
    });
  }

  return followUps.slice(0, 3);
}

// ── Chat export ─────────────────────────────────────────────────────

export type ChatExportFormat = 'markdown' | 'json';

function exportChatAsMarkdown(messages: ChatMessage[], campaignName: string): string {
  const header = `# AI Assistant Chat – ${campaignName}\n_Exported ${new Date().toLocaleDateString()}_\n\n---\n\n`;
  const body = messages
    .map((m) => {
      const label = m.role === 'user' ? '**You**' : '**Assistant**';
      return `${label}\n\n${m.content}\n`;
    })
    .join('\n---\n\n');
  return header + body;
}

function exportChatAsJson(messages: ChatMessage[], campaignName: string): string {
  return JSON.stringify(
    {
      campaign: campaignName,
      exportedAt: new Date().toISOString(),
      messageCount: messages.length,
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
    },
    null,
    2
  );
}

// ── P2-8: Entity cross-reference parser ─────────────────────────────

export interface EntityReference {
  type: 'npc' | 'location' | 'org' | 'plot' | 'wiki';
  name: string;
  raw: string;
}

/** Parse [[type:Name]] cross-references from assistant responses. */
export function parseEntityReferences(text: string): EntityReference[] {
  const refs: EntityReference[] = [];
  const regex = /\[\[(npc|location|org|plot|wiki):([^\]]+)\]\]/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    refs.push({
      type: match[1] as EntityReference['type'],
      name: match[2].trim(),
      raw: match[0],
    });
  }
  return refs;
}

// ── P3-11: Conversation branching types ─────────────────────────────

export interface ConversationBranch {
  id: string;
  label: string;
  parentBranchId: string | null;
  branchPointIndex: number;
  messages: ChatMessage[];
  createdAt: string;
}

function generateBranchId(): string {
  return `branch-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

function parseStoredBranches(value: string | null): ConversationBranch[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (b: unknown): b is ConversationBranch =>
        !!b && typeof b === 'object' && 'id' in b && 'messages' in b
    );
  } catch {
    return [];
  }
}

// ── P2-9: Workflow step tracking types ──────────────────────────────

export interface WorkflowProgress {
  workflowId: string;
  isRunning: boolean;
  currentStep: string;
  steps: Array<{ stepId: string; label: string; content: string; status: 'pending' | 'running' | 'complete' }>;
}

// ── Main hook ───────────────────────────────────────────────────────

export function useAssistant(campaignData: CampaignData, activeTab = 'overview', onDataChanged?: () => void | Promise<void>) {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() => {
    try {
      const stored = localStorage.getItem(CHAT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed.slice(-MAX_STORED_MESSAGES) : [];
      }
    } catch {
      // Ignore parse errors
    }
    return [];
  });
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [agentStatus, setAgentStatus] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [offlineQueue, setOfflineQueue] = useState<OfflineAssistantQueueItem[]>(() => {
    try {
      return parseStoredOfflineQueue(localStorage.getItem(OFFLINE_QUEUE_STORAGE_KEY));
    } catch {
      return [];
    }
  });
  const [isQueueSyncing, setIsQueueSyncing] = useState(false);
  const [queueSyncError, setQueueSyncError] = useState('');
  const [lastQueueSyncAt, setLastQueueSyncAt] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(OFFLINE_QUEUE_LAST_SYNC_KEY);
      return stored && stored.trim() ? stored : null;
    } catch {
      return null;
    }
  });
  const [suggestedItems, setSuggestedItems] = useState<SuggestedItem[]>([]);
  const [followUpSuggestions, setFollowUpSuggestions] = useState<FollowUpSuggestion[]>([]);
  const [editingSuggestion, setEditingSuggestion] = useState<{ index: number; item: SuggestedItem } | null>(null);

  // P2-8: Entity references from latest response
  const [entityReferences, setEntityReferences] = useState<EntityReference[]>([]);

  // P2-9: GM mode and workflow state
  const [gmMode, setGmMode] = useState(false);
  const [contextMode, setContextMode] = useState<'gm' | 'player' | 'shared'>('gm');
  const [workflowProgress, setWorkflowProgress] = useState<WorkflowProgress | null>(null);

  // P3-11: Conversation branches
  const [branches, setBranches] = useState<ConversationBranch[]>(() => {
    try {
      return parseStoredBranches(localStorage.getItem(BRANCH_STORAGE_KEY));
    } catch {
      return [];
    }
  });
  const [activeBranchId, setActiveBranchId] = useState<string | null>(null);

  // CRUD operations state
  const [pendingOperations, setPendingOperations] = useState<AssistantOperation[]>([]);
  const [executedOperations, setExecutedOperations] = useState<AssistantOperation[]>([]);
  const [autoExecuteMode, setAutoExecuteMode] = useState(false);

  // Thread persistence state
  const [activeThreadId, setActiveThreadId] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem(THREAD_ID_STORAGE_KEY);
      if (stored) {
        const parsed = Number.parseInt(stored, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      }
    } catch {
      // Ignore
    }
    return null;
  });

  // Context pins state
  const [contextPins, setContextPins] = useState<ContextPin[]>([]);

  // P3-12: NL search
  const [searchResults, setSearchResults] = useState<NLSearchResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Ref for stable gmMode/contextMode/autoExecute/threadId in callbacks
  const gmModeRef = useRef(gmMode);
  gmModeRef.current = gmMode;
  const contextModeRef = useRef(contextMode);
  contextModeRef.current = contextMode;
  const autoExecuteModeRef = useRef(autoExecuteMode);
  autoExecuteModeRef.current = autoExecuteMode;
  const activeThreadIdRef = useRef(activeThreadId);
  activeThreadIdRef.current = activeThreadId;

  // Persist chat messages to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chatMessages.slice(-MAX_STORED_MESSAGES)));
    } catch {
      // Ignore storage errors
    }
  }, [chatMessages]);

  useEffect(() => {
    try {
      localStorage.setItem(OFFLINE_QUEUE_STORAGE_KEY, JSON.stringify(offlineQueue.slice(-MAX_QUEUED_PROMPTS)));
    } catch {
      // Ignore storage errors
    }
  }, [offlineQueue]);

  // Persist branches to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(BRANCH_STORAGE_KEY, JSON.stringify(branches.slice(-10)));
    } catch {
      // Ignore storage errors
    }
  }, [branches]);

  // Persist activeThreadId to localStorage
  useEffect(() => {
    try {
      if (activeThreadId) {
        localStorage.setItem(THREAD_ID_STORAGE_KEY, String(activeThreadId));
      } else {
        localStorage.removeItem(THREAD_ID_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  }, [activeThreadId]);

  // Load context pins on mount
  useEffect(() => {
    api.getContextPins().then((result) => {
      setContextPins(result.items);
    }).catch(() => {
      // Non-critical
    });
  }, []);

  useEffect(() => {
    try {
      if (lastQueueSyncAt) {
        localStorage.setItem(OFFLINE_QUEUE_LAST_SYNC_KEY, lastQueueSyncAt);
      } else {
        localStorage.removeItem(OFFLINE_QUEUE_LAST_SYNC_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  }, [lastQueueSyncAt]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const queuePrompt = useCallback(
    (
      prompt: string,
      source: OfflineAssistantQueueItem['source'],
      messageAlreadyInChat: boolean,
      attempts = 0
    ): OfflineAssistantQueueItem => {
      const item: OfflineAssistantQueueItem = {
        id: `queue-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        prompt: prompt.trim(),
        queuedAt: new Date().toISOString(),
        source,
        attempts,
        messageAlreadyInChat,
      };
      setOfflineQueue((prev) => [...prev, item].slice(-MAX_QUEUED_PROMPTS));
      return item;
    },
    []
  );

  const executeOperations = useCallback(async (ops: AssistantOperation[]) => {
    try {
      const result = await api.executeAssistantOperations(
        ops.map((op) => ({
          action: op.action,
          entityType: op.entityType,
          entityId: op.entityId,
          data: op.data,
          parentId: op.parentId,
        }))
      );

      const executed: AssistantOperation[] = [];
      for (let i = 0; i < ops.length; i++) {
        const res = result.results[i];
        if (res?.success) {
          executed.push({
            ...ops[i],
            status: 'executed',
            entityId: res.entityId ?? ops[i].entityId,
            undoOperationId: res.undoOperationId,
            executedAt: new Date().toISOString(),
          });
        } else {
          executed.push({
            ...ops[i],
            status: 'failed',
            error: res?.error ?? 'Unknown error',
          });
        }
      }

      setExecutedOperations((prev) => [...prev, ...executed]);
      setPendingOperations((prev) =>
        prev.filter((p) => !ops.some((o) => o.id === p.id))
      );

      // Refresh campaign data after successful operations
      if (executed.some((op) => op.status === 'executed') && onDataChanged) {
        void onDataChanged();
      }

      return executed;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Execution failed';
      const failed = ops.map((op) => ({ ...op, status: 'failed' as const, error: message }));
      setExecutedOperations((prev) => [...prev, ...failed]);
      setPendingOperations((prev) =>
        prev.filter((p) => !ops.some((o) => o.id === p.id))
      );
      return failed;
    }
  }, []);

  const handleResponseSuggestions = useCallback(
    (content: string, toolCalls: AssistantToolCallResult[]) => {
      // Merge tag-parsed suggestions with tool call suggestions
      const tagSuggestions = parseTagSuggestions(content, campaignData.sessionNumber);
      const toolSuggestions = toolCallsToSuggestions(toolCalls);
      setSuggestedItems([...tagSuggestions, ...toolSuggestions]);

      // P2-8: Extract entity cross-references
      setEntityReferences(parseEntityReferences(content));

      // Use AI-generated follow-ups from tool calls if available, else fall back to rules
      const aiFollowUps = toolCallsToFollowUps(toolCalls);
      if (aiFollowUps.length > 0) {
        setFollowUpSuggestions(aiFollowUps);
      } else {
        setFollowUpSuggestions(generateRuleBasedFollowUps(content));
      }

      // Process CRUD tool calls into operations
      const crudOps = toolCallsToCrudOperations(toolCalls);
      if (crudOps.length > 0) {
        const autoOps = crudOps.filter((op) => (autoExecuteModeRef.current || op.autoExecute) && op.action !== 'delete');
        const manualOps = crudOps.filter((op) => !autoOps.includes(op));

        if (manualOps.length > 0) {
          setPendingOperations((prev) => [...prev, ...manualOps]);
        }
        if (autoOps.length > 0) {
          void executeOperations(autoOps);
        }
      }
    },
    [campaignData.sessionNumber, executeOperations]
  );

  const sendChatMessage = useCallback(async (
    overrideMessage?: string,
    options: SendMessageOptions = {}
  ): Promise<SendMessageStatus> => {
    const messageToSend = overrideMessage || chatInput.trim();
    if (!messageToSend || isChatLoading) return 'failed';

    const source = options.source ?? 'chat';
    const skipUserMessage = Boolean(options.skipUserMessage);
    const shouldRequeueOnConnectivityLoss = options.requeueOnConnectivityLoss ?? true;

    if (!overrideMessage && !skipUserMessage) {
      setChatInput('');
    }

    const trimmedMessage = messageToSend.trim();
    const assistantQueueNotice = `Queued offline prompt: "${truncatePrompt(trimmedMessage)}". It will sync automatically once you reconnect.`;

    if (!isOnline) {
      queuePrompt(trimmedMessage, source, false);
      setChatMessages((prev) => [...prev, { role: 'assistant', content: assistantQueueNotice }]);
      return 'queued';
    }

    const newMessages: ChatMessage[] = skipUserMessage
      ? [...chatMessages]
      : [...chatMessages, { role: 'user', content: trimmedMessage }];

    if (!skipUserMessage) {
      setChatMessages(newMessages);
    }
    setIsChatLoading(true);
    setFollowUpSuggestions([]);

    try {
      // Send only user/assistant messages to server; system prompt built server-side
      const apiMessages = newMessages
        .filter((m) => m.content.trim().length > 0)
        .map((m) => ({ role: m.role, content: m.content }));

      // Add placeholder assistant message for streaming
      const streamingMessage: ChatMessage = { role: 'assistant', content: '' };
      setChatMessages([...newMessages, streamingMessage]);

      let streamedContent = '';
      let receivedToolCalls: AssistantToolCallResult[] = [];

      try {
        // Try streaming first with server-side context + tools
        const result = await api.streamAssistantMessage(
          apiMessages,
          (chunk) => {
            streamedContent += chunk;
            setChatMessages([...newMessages, { role: 'assistant', content: streamedContent }]);
          },
          gmModeRef.current ? 0.6 : 0.8,
          {
            useServerContext: true,
            activeTab,
            useTools: true,
            gmMode: gmModeRef.current,
            contextMode: contextModeRef.current,
            threadId: activeThreadIdRef.current ?? undefined,
            onToolCalls: (toolCalls) => {
              receivedToolCalls = toolCalls;
            },
            onAgentStatus: (status) => {
              setAgentStatus(status);
            },
          }
        );

        receivedToolCalls = result.toolCalls;
        // Capture threadId from server (auto-created or existing)
        if (result.threadId && !activeThreadIdRef.current) {
          setActiveThreadId(result.threadId);
        }
        // When AI returns only tool calls with no text, show a summary
        if (!streamedContent && receivedToolCalls.length > 0) {
          const ops = toolCallsToCrudOperations(receivedToolCalls);
          const summaryParts = ops.map((op) => {
            const label = op.entityType.replace(/_/g, ' ');
            const name = op.data?.name || op.data?.title || op.data?.character_name || '';
            return `**${op.action}** ${label}${name ? `: ${name}` : ''}`;
          });
          streamedContent = summaryParts.length > 0
            ? `Proposed changes — check the **Operations** panel to review:\n${summaryParts.map((s) => `- ${s}`).join('\n')}`
            : 'Done — check the **Operations** panel for details.';
          setChatMessages([...newMessages, { role: 'assistant', content: streamedContent }]);
        }
        handleResponseSuggestions(streamedContent, receivedToolCalls);
      } catch {
        // Fall back to non-streaming if streaming fails
        const result = await api.sendAssistantMessage(
          apiMessages,
          gmModeRef.current ? 0.6 : 0.8,
          {
            useServerContext: true,
            activeTab,
            useTools: true,
            gmMode: gmModeRef.current,
            contextMode: contextModeRef.current,
            threadId: activeThreadIdRef.current ?? undefined,
          }
        );
        if (result.threadId && !activeThreadIdRef.current) {
          setActiveThreadId(result.threadId);
        }
        if (result.content) {
          setChatMessages([...newMessages, { role: 'assistant', content: result.content }]);
          handleResponseSuggestions(result.content, result.toolCalls);
        }
      }
      return 'sent';
    } catch (error) {
      console.error('Chat error:', error);
      if (isConnectivityError(error)) {
        if (shouldRequeueOnConnectivityLoss) {
          queuePrompt(trimmedMessage, source, !skipUserMessage, skipUserMessage ? 1 : 0);
        }
        setChatMessages([
          ...newMessages,
          {
            role: 'assistant',
            content: assistantQueueNotice,
          },
        ]);
        return 'queued';
      }

      setChatMessages([
        ...newMessages,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again or check the server configuration.',
        },
      ]);
      return 'failed';
    } finally {
      setIsChatLoading(false);
      setAgentStatus(null);
    }
  }, [
    chatInput,
    chatMessages,
    isChatLoading,
    isOnline,
    activeTab,
    handleResponseSuggestions,
    queuePrompt,
  ]);

  const removeQueuedPrompt = useCallback((itemId: string) => {
    setOfflineQueue((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  const clearOfflineQueue = useCallback(() => {
    setOfflineQueue([]);
    setQueueSyncError('');
  }, []);

  const syncOfflineQueue = useCallback(async () => {
    if (isQueueSyncing || offlineQueue.length === 0) return;
    if (!isOnline) {
      setQueueSyncError('Still offline. Reconnect to sync queued prompts.');
      return;
    }

    setIsQueueSyncing(true);
    setQueueSyncError('');
    let processed = 0;

    for (const item of offlineQueue) {
      setOfflineQueue((prev) =>
        prev.map((entry) =>
          entry.id === item.id
            ? {
                ...entry,
                attempts: entry.attempts + 1,
              }
            : entry
        )
      );

      const status = await sendChatMessage(item.prompt, {
        source: 'retry',
        skipUserMessage: item.messageAlreadyInChat,
        requeueOnConnectivityLoss: false,
      });

      if (status !== 'sent') {
        setQueueSyncError(
          status === 'queued'
            ? 'Connection dropped during sync. Remaining prompts stay queued.'
            : 'Unable to sync all queued prompts. Please retry.'
        );
        break;
      }

      processed += 1;
      setOfflineQueue((prev) => prev.filter((entry) => entry.id !== item.id));
    }

    if (processed > 0) {
      const timestamp = new Date().toISOString();
      setLastQueueSyncAt(timestamp);
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Synced ${processed} queued prompt${processed === 1 ? '' : 's'} from offline mode.`,
        },
      ]);
    }
    setIsQueueSyncing(false);
  }, [isOnline, isQueueSyncing, offlineQueue, sendChatMessage]);

  useEffect(() => {
    if (!isOnline || isQueueSyncing || isChatLoading || offlineQueue.length === 0) return;
    void syncOfflineQueue();
  }, [isOnline, isQueueSyncing, isChatLoading, offlineQueue.length, syncOfflineQueue]);

  const sendQuickAction = useCallback((action: QuickAction) => {
    void sendChatMessage(action.prompt, { source: 'quick_action' });
  }, [sendChatMessage]);

  const sendFollowUp = useCallback((followUp: FollowUpSuggestion) => {
    void sendChatMessage(followUp.prompt, { source: 'follow_up' });
  }, [sendChatMessage]);

  const clearChat = useCallback(() => {
    setChatMessages([]);
    setSuggestedItems([]);
    setFollowUpSuggestions([]);
    setActiveThreadId(null);
    localStorage.removeItem(CHAT_STORAGE_KEY);
    localStorage.removeItem(THREAD_ID_STORAGE_KEY);
  }, []);

  const removeSuggestion = useCallback((index: number) => {
    setSuggestedItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const startEditingSuggestion = useCallback((index: number) => {
    setEditingSuggestion({ index, item: { ...suggestedItems[index] } });
  }, [suggestedItems]);

  const updateEditingSuggestion = useCallback((updates: Partial<SuggestedItem>) => {
    if (editingSuggestion) {
      setEditingSuggestion({
        ...editingSuggestion,
        item: { ...editingSuggestion.item, ...updates } as SuggestedItem,
      });
    }
  }, [editingSuggestion]);

  const saveEditedSuggestion = useCallback(() => {
    if (editingSuggestion) {
      setSuggestedItems((prev) => {
        const updated = [...prev];
        updated[editingSuggestion.index] = editingSuggestion.item;
        return updated;
      });
      setEditingSuggestion(null);
    }
  }, [editingSuggestion]);

  const cancelEditingSuggestion = useCallback(() => {
    setEditingSuggestion(null);
  }, []);

  // ── Chat export (P3-14) ──────────────────────────────────────────
  const exportChat = useCallback(
    (format: ChatExportFormat = 'markdown') => {
      const campaignName = campaignData.name || 'Campaign';
      const content =
        format === 'json'
          ? exportChatAsJson(chatMessages, campaignName)
          : exportChatAsMarkdown(chatMessages, campaignName);

      const ext = format === 'json' ? 'json' : 'md';
      const mimeType = format === 'json' ? 'application/json' : 'text/markdown';
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assistant-chat-${Date.now()}.${ext}`;
      link.click();
      URL.revokeObjectURL(url);
    },
    [chatMessages, campaignData.name]
  );

  // ── P2-7: Workflow execution ──────────────────────────────────────
  const runWorkflow = useCallback(async (workflowId: string) => {
    if (isChatLoading) return;
    setIsChatLoading(true);

    const progress: WorkflowProgress = {
      workflowId,
      isRunning: true,
      currentStep: '',
      steps: [],
    };
    setWorkflowProgress(progress);

    setChatMessages((prev) => [...prev, { role: 'assistant', content: `Starting workflow: **${workflowId}**...` }]);

    try {
      await api.runWorkflow(workflowId, (event: WorkflowStepEvent) => {
        if (event.type === 'step_start') {
          progress.currentStep = event.label || '';
          progress.steps.push({
            stepId: event.stepId || '',
            label: event.label || '',
            content: '',
            status: 'running',
          });
          setWorkflowProgress({ ...progress });
        } else if (event.type === 'step_complete') {
          const step = progress.steps.find((s) => s.stepId === event.stepId);
          if (step) {
            step.content = event.content || '';
            step.status = 'complete';
          }
          setWorkflowProgress({ ...progress });

          // Add step result to chat
          setChatMessages((prev) => [
            ...prev,
            { role: 'assistant', content: `### ${event.label}\n\n${event.content || ''}` },
          ]);
        } else if (event.type === 'workflow_complete') {
          progress.isRunning = false;
          setWorkflowProgress({ ...progress });
        } else if (event.type === 'error') {
          setChatMessages((prev) => [
            ...prev,
            { role: 'assistant', content: `Workflow error: ${event.error || 'Unknown error'}` },
          ]);
        }
      });
    } catch (error) {
      console.error('Workflow error:', error);
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Workflow execution failed. Please try again.' },
      ]);
    } finally {
      setIsChatLoading(false);
      setWorkflowProgress(null);
    }
  }, [isChatLoading]);

  // ── P2-10: Entity extraction ──────────────────────────────────────
  const extractEntities = useCallback(async (text: string): Promise<EntityExtractionResult | null> => {
    try {
      return await api.extractEntities(text);
    } catch (error) {
      console.error('Entity extraction error:', error);
      return null;
    }
  }, []);

  // ── P3-11: Conversation branching ─────────────────────────────────
  const createBranch = useCallback((label: string, branchPointIndex?: number) => {
    const pointIndex = branchPointIndex ?? chatMessages.length - 1;
    const branch: ConversationBranch = {
      id: generateBranchId(),
      label,
      parentBranchId: activeBranchId,
      branchPointIndex: pointIndex,
      messages: chatMessages.slice(0, pointIndex + 1),
      createdAt: new Date().toISOString(),
    };
    setBranches((prev) => [...prev, branch]);
    return branch;
  }, [chatMessages, activeBranchId]);

  const switchToBranch = useCallback((branchId: string) => {
    const branch = branches.find((b) => b.id === branchId);
    if (!branch) return;

    // Save current conversation as a branch if not already saved
    if (!activeBranchId) {
      const mainBranch: ConversationBranch = {
        id: generateBranchId(),
        label: 'Main conversation',
        parentBranchId: null,
        branchPointIndex: 0,
        messages: [...chatMessages],
        createdAt: new Date().toISOString(),
      };
      setBranches((prev) => [...prev, mainBranch]);
    }

    setChatMessages(branch.messages);
    setActiveBranchId(branchId);
    setSuggestedItems([]);
    setFollowUpSuggestions([]);
  }, [branches, chatMessages, activeBranchId]);

  const deleteBranch = useCallback((branchId: string) => {
    setBranches((prev) => prev.filter((b) => b.id !== branchId));
    if (activeBranchId === branchId) {
      setActiveBranchId(null);
    }
  }, [activeBranchId]);

  // ── P3-12: Natural language search ────────────────────────────────
  const nlSearch = useCallback(async (query: string) => {
    setIsSearching(true);
    try {
      const result = await api.nlSearch(query);
      setSearchResults(result);
      return result;
    } catch (error) {
      console.error('NL search error:', error);
      setSearchResults(null);
      return null;
    } finally {
      setIsSearching(false);
    }
  }, []);

  const clearSearchResults = useCallback(() => {
    setSearchResults(null);
  }, []);

  // ── Universal CRUD operations ──────────────────────────────────────

  const approveOperation = useCallback((operationId: string) => {
    const op = pendingOperations.find((p) => p.id === operationId);
    if (!op) return;
    void executeOperations([op]);
  }, [pendingOperations, executeOperations]);

  const approveAllOperations = useCallback(() => {
    if (pendingOperations.length === 0) return;
    void executeOperations(pendingOperations);
  }, [pendingOperations, executeOperations]);

  const rejectOperation = useCallback((operationId: string) => {
    setPendingOperations((prev) =>
      prev.filter((p) => p.id !== operationId)
    );
  }, []);

  const rejectAllOperations = useCallback(() => {
    setPendingOperations([]);
  }, []);

  const undoOperation = useCallback(async (operationId: string) => {
    const op = executedOperations.find((e) => e.id === operationId);
    if (!op?.undoOperationId) return;

    try {
      const result = await api.undoAssistantOperation(op.undoOperationId);
      if (result.success) {
        setExecutedOperations((prev) =>
          prev.map((e) => e.id === operationId ? { ...e, status: 'undone' } : e)
        );
        if (onDataChanged) void onDataChanged();
      }
    } catch (error) {
      console.error('Undo failed:', error);
    }
  }, [executedOperations, onDataChanged]);

  const clearExecutedOperations = useCallback(() => {
    setExecutedOperations([]);
  }, []);

  // ── Context pins management ────────────────────────────────────
  const loadContextPins = useCallback(async () => {
    try {
      const result = await api.getContextPins();
      setContextPins(result.items);
    } catch {
      // Non-critical
    }
  }, []);

  const addContextPin = useCallback(async (entityType: string, entityId: number) => {
    try {
      const pin = await api.createContextPin(entityType, entityId);
      setContextPins((prev) => [...prev, pin]);
      return pin;
    } catch {
      return null;
    }
  }, []);

  const removeContextPin = useCallback(async (pinId: number) => {
    try {
      await api.deleteContextPin(pinId);
      setContextPins((prev) => prev.filter((p) => p.id !== pinId));
    } catch {
      // Non-critical
    }
  }, []);

  const clearAllContextPins = useCallback(async () => {
    for (const pin of contextPins) {
      try {
        await api.deleteContextPin(pin.id);
      } catch {
        // Continue
      }
    }
    setContextPins([]);
  }, [contextPins]);

  return {
    chatMessages,
    chatInput,
    setChatInput,
    isChatLoading,
    agentStatus,
    isOnline,
    offlineQueue,
    isQueueSyncing,
    queueSyncError,
    lastQueueSyncAt,
    suggestedItems,
    followUpSuggestions,
    editingSuggestion,
    sendChatMessage,
    syncOfflineQueue,
    removeQueuedPrompt,
    clearOfflineQueue,
    sendQuickAction,
    sendFollowUp,
    clearChat,
    removeSuggestion,
    startEditingSuggestion,
    updateEditingSuggestion,
    saveEditedSuggestion,
    cancelEditingSuggestion,
    exportChat,
    quickActions: QUICK_ACTIONS,

    // P2-7: Workflows
    runWorkflow,
    workflowProgress,

    // P2-8: Entity cross-references
    entityReferences,

    // P2-9: GM mode
    gmMode,
    setGmMode,

    // P2-10: Entity extraction
    extractEntities,

    // P3-11: Conversation branching
    branches,
    activeBranchId,
    createBranch,
    switchToBranch,
    deleteBranch,

    // P3-12: NL search
    searchResults,
    isSearching,
    nlSearch,
    clearSearchResults,

    // P3-13: Context mode
    contextMode,
    setContextMode,

    // Universal CRUD operations
    pendingOperations,
    executedOperations,
    autoExecuteMode,
    setAutoExecuteMode,
    approveOperation,
    approveAllOperations,
    rejectOperation,
    rejectAllOperations,
    undoOperation,
    clearExecutedOperations,

    // Thread persistence
    activeThreadId,

    // Context pins
    contextPins,
    loadContextPins,
    addContextPin,
    removeContextPin,
    clearAllContextPins,
  };
}
